/** @jsx jsx */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { jsx } from '@emotion/core';
import styled from '@emotion/styled';
import { Typography, useTheme } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';

import { ICustomTheme, IChallengeList } from 'interfaces';
import {
  challengesStatusSelector,
  sortedDeclaredChallengesSelector,
} from 'reducers/challenge.reducer';
import Action from '../Action';
import { LOADING_STATUS } from 'constants/index';

type Theme = {
  theme?: ICustomTheme;
};

const CustomSkeleton = styled.div<Theme>(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: 1,
  backgroundColor: theme.colors.white,
  boxShadow: theme.shadows[2],
  padding: theme.spacing(0, 0, 2, 2),
}));

const StatusContainer = styled.div<Theme>(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginRight: theme.spacing(1.5),
}));

const ActionSkeleton = () => {
  const theme: ICustomTheme = useTheme();

  return (
    <CustomSkeleton>
      <StatusContainer>
        <Skeleton variant="text" width={80} height={20} />
      </StatusContainer>
      <Skeleton variant="text" width={80} height={16} />
      <Skeleton
        variant="text"
        width={140}
        height={16}
        css={{ marginTop: theme.spacing(1) }}
      />
      <Skeleton
        variant="rect"
        width={160}
        height={40}
        css={{ marginTop: theme.spacing(1) }}
      />
    </CustomSkeleton>
  );
};

const EmptyStateActions = styled(Typography)<Theme>(({ theme }) => ({
  padding: theme.spacing(3),
  color: theme.colors.grey.dark,
}));

const List = () => {
  const { t } = useTranslation();

  const declaredChallengesLoadingStatus = useSelector(challengesStatusSelector);
  const isChallengesOnPendingStatus =
    declaredChallengesLoadingStatus === LOADING_STATUS.PENDING;
  const isChallengesOnResolvedStatus =
    declaredChallengesLoadingStatus === LOADING_STATUS.RESOLVED;
  const declaredChallenges = useSelector(sortedDeclaredChallengesSelector);

  if (isChallengesOnPendingStatus) {
    return (
      <React.Fragment>
        <ActionSkeleton />
        <ActionSkeleton />
        <ActionSkeleton />
        <ActionSkeleton />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {isChallengesOnResolvedStatus && !!declaredChallenges.length ? (
        <div>
          {declaredChallenges.map((action: IChallengeList) => {
            return <Action action={action} key={action.id} />;
          })}
        </div>
      ) : (
        <EmptyStateActions variant="h5">
          {t('challenges.declaration.history.no_declaration')}
        </EmptyStateActions>
      )}
    </React.Fragment>
  );
};

export default List;
